import { type SectionAssignment } from '@breakoutlearning/firebase-repository/models/SectionAssignment'
import { CompletionMetric } from '@breakoutlearning/firebase-repository/models/SectionAssignment'
import classNames from 'classnames'
import { Trans, useTranslation } from 'react-i18next'

export const GradingTooltipFinalGrade = ({
  scalars,
  hasQuizzes,
}: {
  scalars: NonNullable<SectionAssignment['data']['assignmentGradingScalars']>
  hasQuizzes: boolean
}) => {
  const { t } = useTranslation()

  const {
    quizScore,
    attendanceScore,
    participationScore,
    totalRubricScore,
  }: typeof scalars = {
    quizScore: Math.round(scalars.quizScore * 100),
    attendanceScore: Math.round(scalars.attendanceScore * 100),
    participationScore: Math.round(scalars.participationScore * 100),
    totalRubricScore: Math.round(scalars.totalRubricScore * 100),
  }

  return (
    <TooltipWrapper className="max-w-[200px]">
      <ul className="list-outside list-disc pl-3">
        {hasQuizzes && (
          <li>
            {t('instructor_class.final_score_tooltip.quiz_score', {
              quizScore,
            })}
          </li>
        )}
        <li>
          {t('instructor_class.final_score_tooltip.attendance', {
            attendanceScore,
          })}
        </li>
        <li>
          {t('instructor_class.final_score_tooltip.completion', {
            participationScore,
          })}
        </li>
        <li>
          {t('instructor_class.final_score_tooltip.critical_thinking', {
            totalRubricScore,
          })}
        </li>
      </ul>
    </TooltipWrapper>
  )
}

export const GradingTooltipAttendance = () => {
  const { t } = useTranslation()
  return (
    <TooltipWrapper className="max-w-[350px]">
      <ul className="list-outside list-disc pl-3">
        <li>
          <Trans
            i18nKey="instructor_class.attendance_tooltip.1"
            components={{ bold: <strong className="font-bold" /> }}
          />
        </li>
        <li>{t('instructor_class.attendance_tooltip.2')}</li>
      </ul>
    </TooltipWrapper>
  )
}

export const GradingToolTipQuizScore = () => {
  const { t } = useTranslation()
  return (
    <TooltipWrapper>{t('instructor_class.quiz_score_tooltip')}</TooltipWrapper>
  )
}

export const GradingTooltipCompletion = ({
  metric,
}: {
  metric?: CompletionMetric
} = {}) => {
  const { t } = useTranslation()
  // Camera-on grading is scored differently, so the tooltip describes the
  // configured metric.
  const base =
    metric === CompletionMetric.cameraOnTime
      ? 'instructor_class.completion_camera_tooltip'
      : 'instructor_class.completion_tooltip'
  return (
    <TooltipWrapper className="max-w-[350px]">
      <ul className="list-outside list-disc pl-3">
        <li>{t(`${base}.1`)}</li>
        <li>{t(`${base}.2`)}</li>
        <li>{t(`${base}.3`)}</li>
      </ul>
    </TooltipWrapper>
  )
}

// Config-context Completion (i): generic copy, distinct from the gradebook
// tooltip (GradingTooltipCompletion), which describes the configured metric.
export const GradingTooltipCompletionConfig = () => {
  const { t } = useTranslation()
  return (
    <TooltipWrapper className="max-w-[350px]">
      {t('instructor_class.completion_config_tooltip')}
    </TooltipWrapper>
  )
}

export const GradingTooltipCustomize = () => {
  const { t } = useTranslation()
  return (
    <TooltipWrapper className="max-w-[350px]">
      {t('instructor_class.completion_customize_tooltip')}
    </TooltipWrapper>
  )
}

export const GradingTooltipTimeInSession = () => {
  const { t } = useTranslation()
  return (
    <TooltipWrapper className="max-w-[350px]">
      {t('instructor_class.completion_metric_time_tooltip')}
    </TooltipWrapper>
  )
}

export const GradingTooltipCameraOnTime = () => {
  const { t } = useTranslation()
  return (
    <TooltipWrapper className="max-w-[350px]">
      {t('instructor_class.completion_metric_camera_tooltip')}
    </TooltipWrapper>
  )
}

export const GradingTooltipCriticalThinking = ({
  audience = 'instructor',
}: {
  audience?: 'instructor' | 'participant'
} = {}) => {
  const { t } = useTranslation()
  return (
    <TooltipWrapper className="max-w-[300px]">
      <span>{t('instructor_class.critical_thinking_tooltip.1')}</span>
      <ul className="list-outside list-disc pl-5">
        <li>
          {t(
            audience === 'participant'
              ? 'instructor_class.critical_thinking_tooltip.participant'
              : 'instructor_class.critical_thinking_tooltip.2'
          )}
        </li>
      </ul>
    </TooltipWrapper>
  )
}

export const TooltipWrapper = ({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & { children: React.ReactNode }) => {
  return (
    <div
      className={classNames(
        'rounded-lg bg-on-surface-var px-4 py-2 text-body-medium text-core-on-primary',
        className
      )}
      {...props}
    >
      {children}
    </div>
  )
}
